import React, { useEffect, useRef } from 'react'
import { RefreshCw } from 'lucide-react'
import { Message, ThinkingStep } from '../types'
import { ChatMessage } from './ChatMessage'
import { TypingIndicator } from './TypingIndicator'
import { WelcomeScreen } from './WelcomeScreen'
import { useTheme } from '@/context/ThemeContext'
import { LeadActionWelcome } from '@/features/hubspot/components/LeadActionWelcome'
import type { LeadData } from '@/features/hubspot/services/notificationsApi'

interface ChatMessageListProps {
  messages: Message[]
  isTyping: boolean
  isLoadingMessages?: boolean
  userName?: string | undefined
  onSuggestionClick: (text: string) => void | Promise<void>
  currentStatus?: string | null
  pendingSteps?: ThinkingStep[]
  onOpenArtifact?: (id: string, title: string, content: string) => void
  onTriggerAction?: (type: string, payload: any) => void
  isRetryable?: boolean
  onRetry?: () => void
  /** When set, replaces the generic WelcomeScreen with the LeadActionWelcome */
  leadContext?: LeadData | null
  onLeadActionSelect?: (promptText: string) => void
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isTyping,
  isLoadingMessages = false,
  userName,
  onSuggestionClick,
  currentStatus,
  pendingSteps = [],
  onOpenArtifact,
  onTriggerAction,
  isRetryable = false,
  onRetry,
  leadContext,
  onLeadActionSelect,
}) => {
  const { currentTheme } = useTheme()
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, isTyping])

  // Scroll to bottom when streaming content grows
  useEffect(() => {
    const lastMsg = messages[messages.length - 1]
    if (lastMsg?.isStreaming) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' })
    }
  }, [messages])

  const hasMessages = messages.length > 0 || isTyping || isLoadingMessages

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
      style={{ backgroundColor: currentTheme.primary }}
    >
      {!hasMessages ? (
        <div className="flex flex-col min-h-full">
          {leadContext && onLeadActionSelect ? (
            <LeadActionWelcome lead={leadContext} onSelectAction={onLeadActionSelect} />
          ) : (
            <WelcomeScreen userName={userName} onSuggestionClick={onSuggestionClick} />
          )}
        </div>
      ) : (
        <div className="py-4">
          {isLoadingMessages ? (
            <div className="flex items-start gap-3 px-4 py-2 max-w-3xl mx-auto">
              <div
                className="w-8 h-8 rounded-full shrink-0 animate-pulse"
                style={{ backgroundColor: currentTheme.border }}
              />
              <div className="flex flex-col gap-2 flex-1 max-w-[60%]">
                <div className="h-3 rounded-full animate-pulse" style={{ backgroundColor: currentTheme.border, width: '75%' }} />
                <div className="h-3 rounded-full animate-pulse" style={{ backgroundColor: currentTheme.border, width: '55%' }} />
                <div className="h-3 rounded-full animate-pulse" style={{ backgroundColor: currentTheme.border, width: '65%' }} />
              </div>
            </div>
          ) : (
            <>
              {messages.map(msg => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onOpenArtifact={onOpenArtifact}
                  onTriggerAction={onTriggerAction}
                  choicesActive={msg.id === messages[messages.length - 1]?.id && !isTyping}
                  onSubmitChoices={onSuggestionClick}
                />
              ))}
              {isTyping && <TypingIndicator status={currentStatus} steps={pendingSteps} />}
              {isRetryable && !isTyping && (
                <div className="px-4 py-2 max-w-3xl mx-auto w-full flex items-center justify-start mt-2">
                  <button
                    onClick={onRetry}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: currentTheme.activeBackground,
                      borderColor: currentTheme.accent,
                      color: currentTheme.accent,
                    }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Retry generation</span>
                  </button>
                </div>
              )}
            </>
          )}
          <div ref={bottomRef} className="h-4" />
        </div>
      )}
    </div>
  )
}
